import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Banner } from './Common';

/**
 * Refresh the stored figures for a chosen period.
 *
 * Shown on the dashboard as well as under Stored data, because a director
 * wanting last month's numbers should not have to ask someone. The wording here
 * is deliberately plain -- "refresh the figures", not "sync the datasets" --
 * since most of the people pressing it will not know or care what a dataset is.
 *
 * The server applies an hourly cooldown for non-editors. Refreshing spends an
 * allowance shared across the whole company, and several people refreshing
 * before a Monday meeting would break that night's scheduled update. The
 * message when it refuses says so in those terms rather than mentioning quotas.
 */

interface Step {
  slug: string;
  name: string;
  status: 'pending' | 'running' | 'ok' | 'error' | 'skipped';
  rowCount?: number;
  inserted?: number;
  updated?: number;
  error?: string;
  warnings?: string[];
  note?: string;
}

interface Job {
  id: string;
  status: 'running' | 'done' | 'failed';
  period: string;
  startedAt: string;
  finishedAt?: string;
  actor: string;
  steps: Step[];
}

const PERIODS = [
  { key: 'last-week', label: 'Last full week', hint: 'One week. A couple of minutes.' },
  { key: 'last-4-weeks', label: 'Last 4 weeks', hint: 'Roughly four minutes.' },
  { key: 'last-12-weeks', label: 'Last 12 weeks', hint: 'Ten minutes or so. Start it and leave it.' },
  { key: 'last-26-weeks', label: 'Last 26 weeks', hint: 'Twenty minutes plus. Half a year of history.' },
];

const ICON: Record<Step['status'], string> = {
  pending: '·',
  running: '…',
  ok: '✓',
  error: '✕',
  skipped: '–',
};

export function SyncAll({ onFinished }: { onFinished?: () => void }) {
  const [period, setPeriod] = useState('last-week');
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef<number | null>(null);

  /**
   * Whether THIS component has watched a refresh run.
   *
   * onFinished must fire on the TRANSITION from running to finished, never on
   * simply discovering a completed job. The server keeps the last job in memory,
   * so on every page load the first poll finds it already finished -- and firing
   * onFinished there put the dashboard into a reload loop: reload, poll, see the
   * old finished job, reload. The screen flashed until you navigated away.
   */
  const sawRunning = useRef(false);

  const poll = async () => {
    try {
      const res = await api.get<{ job: Job | null }>('/api/datasets/sync-all/status');
      setJob(res.job);
      if (res.job && res.job.status === 'running') {
        sawRunning.current = true;
        timer.current = window.setTimeout(poll, 2000);
      } else if (res.job && sawRunning.current) {
        sawRunning.current = false;
        onFinished?.();
      }
    } catch (err: any) {
      // A lost poll is not a lost sync -- the job runs on the server. Say that,
      // rather than letting a blip look like a failed back-fill.
      setError('Lost contact while watching progress. The refresh is still running; reopen this page to pick it up again.');
    }
  };

  // Pick up a job already in flight — someone else may have started it, or this
  // tab may have been reopened mid-run.
  useEffect(() => {
    void poll();
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const start = async () => {
    setError(null);
    setStarting(true);
    try {
      await api.post('/api/datasets/sync-all', { period });
      sawRunning.current = true;
      void poll();
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setStarting(false);
    }
  };

  const running = job?.status === 'running';
  const chosen = PERIODS.find((p) => p.key === period);

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>Refresh the figures</h3>
      <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--muted)' }}>
        Fetches fresh numbers from Bullhorn and Dialpad for the period you choose, and adds any weeks
        that are not held yet. Everything updates overnight on its own — use this when you want a longer
        history than the dashboard currently shows, or to pick up a change made today.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'block' }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Period</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} disabled={running}>
            {PERIODS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <button className="primary" onClick={start} disabled={running || starting}>
          {running ? 'Refreshing…' : starting ? 'Starting…' : 'Refresh now'}
        </button>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{chosen?.hint}</span>
      </div>

      {error && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="warn">{error}</Banner>
        </div>
      )}

      {job && (job.status === 'running' || sawRunning.current) && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
            {job.status === 'running'
              ? `Refreshing — ${job.steps.filter((s) => s.status === 'ok' || s.status === 'error').length} of ${job.steps.length} done. You can leave this page; it keeps going.`
              : job.status === 'done'
                ? 'Finished. Reload the page to see the new figures.'
                : 'Finished, with problems. The ones marked ✕ did not complete — the rest did, and those figures are up to date.'}
          </div>
          <table className="data-table">
            <tbody>
              {job.steps.map((step) => (
                <tr key={step.slug}>
                  <td style={{ width: 24 }}>{ICON[step.status]}</td>
                  <td>{step.name}</td>
                  <td style={{ color: 'var(--muted)', fontSize: 12 }}>
                    {step.status === 'ok'
                      ? `${(step.rowCount ?? 0).toLocaleString()} rows · ${step.inserted ?? 0} new, ${step.updated ?? 0} updated`
                      : step.status === 'error'
                        ? step.error
                        : step.status === 'running'
                          ? 'fetching…'
                          : ''}
                    {step.note ? <div style={{ marginTop: 2 }}>{step.note}</div> : null}
                    {step.warnings?.length ? (
                      <div style={{ marginTop: 2, color: 'var(--warn, #a1620a)' }}>
                        {step.warnings.slice(0, 2).map((w) => (
                          <div key={w}>{w}</div>
                        ))}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 2px 0' }}>
            Progress is held in memory, so a deploy mid-run loses this view — anything already ticked has
            been written to the store regardless.
          </p>
        </div>
      )}
    </div>
  );
}
